// Notes are Tiptap documents stored in a Y.XmlFragment. This converts Markdown (what agents write and
// read) to and from that structure using the exact schema the browser editor uses, and checks every
// document before storing it: the editor's sync plugin deletes any element it can't build, so an
// invalid structure would destroy content as soon as a browser opened the note.
import { getSchema } from '@tiptap/core';
import type { Schema } from '@tiptap/pm/model';
import StarterKit from '@tiptap/starter-kit';
import { prosemirrorJSONToYXmlFragment, yXmlFragmentToProsemirrorJSON } from '@tiptap/y-tiptap';
import * as Y from 'yjs';
import { childNodes, docToMarkdown, markdownToDoc, type PMNode } from './markdown.js';
import { appendXmlChildren } from './ydeep.js';

/** The note editor's extensions. NoteCard adds Collaboration and its caret, which add no nodes or marks. */
export const noteExtensions = () => [StarterKit.configure({ undoRedo: false })];

let cached: Schema | null = null;
export function noteSchema(): Schema {
  cached ??= getSchema(noteExtensions());
  return cached;
}

/** All the text in a document: blocks on separate lines. */
function textOf(n: PMNode): string {
  if (n.type === 'text') return n.text ?? '';
  if (n.type === 'hardBreak') return '\n';
  const parts = childNodes(n).map(textOf);
  return n.type === 'paragraph' || n.type === 'heading' || n.type === 'codeBlock' ? parts.join('') : parts.join('\n');
}

/** One plain paragraph per line: always valid. */
export function plainDoc(text: string): PMNode {
  return {
    type: 'doc',
    content: text.split('\n').map((line) => (line ? { type: 'paragraph', content: [{ type: 'text', text: line }] } : { type: 'paragraph' })),
  };
}

/** Marks in the schema's order with duplicates and unknown marks dropped (ProseMirror refuses those). */
function normaliseMarks(n: PMNode, order: string[]): PMNode {
  const out: PMNode = { ...n };
  if (n.marks) {
    const seen = new Set<string>();
    const marks = n.marks
      .filter((m) => order.includes(m.type) && !seen.has(m.type) && !!seen.add(m.type))
      .sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));
    if (marks.length) out.marks = marks;
    else delete out.marks;
  }
  if (n.content) out.content = childNodes(n).map((c) => normaliseMarks(c, order));
  return out;
}

/**
 * Tiptap v3's StarterKit includes TrailingNode: as soon as any browser opens a note whose last
 * block isn't a paragraph (a heading, list, quote, code block or rule — common for agent
 * Markdown), its read-only canvas editor appends an empty paragraph, and y-tiptap writes that
 * straight into the shared document — a write nobody asked for, misattributed in History to
 * whoever merely looked (review Important 1). Appending the same empty paragraph here, before
 * the document is ever stored, means TrailingNode finds nothing left to add when a browser does
 * open it: viewing becomes a true no-op. Invisible in Markdown either way (docToMarkdown drops
 * empty paragraphs), so this doesn't change readMarkdown, the 20,000-char limit, or the
 * agentMarkdownSha the 409 conflict check hashes.
 */
function withTrailingParagraph(doc: PMNode): PMNode {
  const blocks = childNodes(doc);
  const last = blocks[blocks.length - 1];
  return last?.type === 'paragraph' ? doc : { ...doc, content: [...blocks, { type: 'paragraph' }] };
}

/** `json` when it fits the note schema, otherwise its text as plain paragraphs. */
export function validNoteDoc(json: PMNode): PMNode {
  const schema = noteSchema();
  const fixed = normaliseMarks(json, Object.keys(schema.marks));
  try {
    schema.nodeFromJSON(fixed).check();
    return withTrailingParagraph(fixed);
  } catch {
    return plainDoc(textOf(json));
  }
}

/** Replaces a note's content with `markdown`. It updates by difference: unchanged blocks are kept. */
export function writeMarkdown(fragment: Y.XmlFragment, markdown: string) {
  prosemirrorJSONToYXmlFragment(noteSchema(), validNoteDoc(markdownToDoc(markdown)), fragment);
}

/** Adds `markdown` after a note's content without touching what's there, so it merges with anyone typing. */
export function appendMarkdown(fragment: Y.XmlFragment, markdown: string) {
  const scratch = new Y.Doc().getXmlFragment('scratch');
  prosemirrorJSONToYXmlFragment(noteSchema(), validNoteDoc(markdownToDoc(markdown)), scratch);
  appendXmlChildren(fragment, scratch);
}

/** A note's content as Markdown (what agents read). */
export function readMarkdown(fragment: Y.XmlFragment): string {
  return docToMarkdown(yXmlFragmentToProsemirrorJSON(fragment) as PMNode);
}
